import { useState, useEffect } from "react";
import { Briefcase, MapPin, Clock, ArrowRight, CheckCircle2, Search, FileText, Lightbulb } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import ScrollReveal from "@/components/ScrollReveal";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const industries = ["All", "IT", "Banking & Finance", "Manufacturing", "Healthcare", "Retail & FMCG", "Construction", "Logistics", "Education", "Pharma"];
const roleTypes = ["All", "Permanent", "Contract"];

const tips = [
  { icon: FileText, title: "Polish Your Resume", desc: "Tailor your resume to each application. Highlight achievements over responsibilities." },
  { icon: Search, title: "Research the Company", desc: "Understand the company culture and values before your interview." },
  { icon: Lightbulb, title: "Prepare for Interviews", desc: "Practice common questions and prepare real examples from your experience." },
];

export default function CareersPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [industry, setIndustry] = useState("All");
  const [roleType, setRoleType] = useState("All");
  const [form, setForm] = useState({ name: "", email: "", phone: "", experience: "", current_role: "", preferred_industry: "", preferred_role_type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, [industry, roleType]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (industry !== "All") params.append("industry", industry);
      if (roleType !== "All") params.append("role_type", roleType);
      const res = await axios.get(`${API}/jobs?${params.toString()}`);
      setJobs(res.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/apply`, form);
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", experience: "", current_role: "", preferred_industry: "", preferred_role_type: "", message: "" });
    } catch (err) {
      console.error(err);
    }
    setSubmitting(false);
  };

  return (
    <div>
      {/* Hero */}
      <section className="bg-[var(--sb-green)] text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <ScrollReveal>
            <span className="text-sm font-bold tracking-[0.15em] uppercase text-white/80 mb-4 block">For Job Seekers</span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight uppercase mb-6">Find Your Next Opportunity</h1>
            <p className="text-green-100 text-base md:text-lg max-w-2xl leading-relaxed">
              Browse open positions across India's top companies. Submit your profile and let our team connect you with the right role.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Job Listings */}
      <section data-testid="job-listings-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <ScrollReveal className="mb-12">
            <span className="text-sm font-bold tracking-[0.15em] uppercase text-[var(--sb-blue)] mb-3 block">Current Openings</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 uppercase mb-8">Job Listings</h2>

            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="w-full sm:w-48">
                <Select value={industry} onValueChange={setIndustry}>
                  <SelectTrigger data-testid="jobs-industry-filter" className="bg-slate-50 border-slate-200 rounded-none">
                    <SelectValue placeholder="Industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map((ind) => (
                      <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full sm:w-48">
                <Select value={roleType} onValueChange={setRoleType}>
                  <SelectTrigger data-testid="jobs-roletype-filter" className="bg-slate-50 border-slate-200 rounded-none">
                    <SelectValue placeholder="Role Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {roleTypes.map((rt) => (
                      <SelectItem key={rt} value={rt}>{rt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </ScrollReveal>

          {/* Listings */}
          {loading ? (
            <div className="text-center py-12 text-slate-500">Loading jobs...</div>
          ) : jobs.length === 0 ? (
            <div data-testid="no-jobs-message" className="text-center py-12 bg-slate-50 border border-slate-200 p-8">
              <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold text-slate-700 uppercase mb-2">No jobs found</h3>
              <p className="text-slate-500 text-sm">Try adjusting your filters or submit your profile below for future opportunities.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((job) => (
                <ScrollReveal key={job.id}>
                  <div data-testid={`job-card-${job.id}`} className="bg-white border border-slate-200 p-6 md:p-8 transition-all duration-300 hover:shadow-[0_10px_40px_-15px_rgba(26,95,168,0.15)] hover:border-[var(--sb-blue)]/30">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3 mb-3">
                          <h3 className="font-heading text-xl font-bold text-slate-900 uppercase">{job.title}</h3>
                          <Badge className={`text-xs ${job.role_type === "Permanent" ? "bg-[var(--sb-blue)] text-white border-0" : "bg-[var(--sb-orange)] text-white border-0"}`}>
                            {job.role_type}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-4 text-slate-500 text-sm mb-3">
                          <span className="flex items-center gap-1"><Briefcase className="w-4 h-4" />{job.company}</span>
                          <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{job.location}</span>
                          <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{job.experience}</span>
                        </div>
                        <p className="text-slate-600 text-sm leading-relaxed">{job.description}</p>
                        {job.requirements.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {job.requirements.map((req) => (
                              <span key={req} className="inline-block px-3 py-1 bg-slate-100 text-slate-600 text-xs font-medium border border-slate-200">{req}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        <span className="inline-block px-4 py-2 bg-slate-100 text-slate-600 text-xs font-medium border border-slate-200">{job.industry}</span>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Resume Submission */}
      <section id="apply" data-testid="resume-submission-section" className="py-20 md:py-32 bg-[#F1F5F9]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <ScrollReveal className="text-center mb-12">
            <span className="text-sm font-bold tracking-[0.15em] uppercase text-[var(--sb-green)] mb-3 block">Join Our Talent Pool</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 uppercase">Submit Your Profile</h2>
            <p className="text-slate-600 mt-4 text-base">Share your details and we'll match you with the right opportunities.</p>
          </ScrollReveal>

          {submitted ? (
            <ScrollReveal>
              <div className="bg-white border border-[var(--sb-green)] p-12 text-center">
                <CheckCircle2 className="w-16 h-16 text-[var(--sb-green)] mx-auto mb-4" />
                <h3 className="font-heading text-2xl font-bold text-slate-900 uppercase mb-2">Profile Submitted!</h3>
                <p className="text-slate-600">Our placement team will review your profile and reach out with matching opportunities.</p>
              </div>
            </ScrollReveal>
          ) : (
            <ScrollReveal>
              <form onSubmit={handleSubmit} className="bg-white border border-slate-200 p-8 md:p-10 space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Full Name *</label>
                    <Input data-testid="apply-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-50 border-slate-200 focus:border-[var(--sb-green)] focus:ring-[var(--sb-green)] rounded-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Email *</label>
                    <Input data-testid="apply-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-slate-50 border-slate-200 focus:border-[var(--sb-green)] focus:ring-[var(--sb-green)] rounded-none" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Phone *</label>
                    <Input data-testid="apply-phone-input" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-slate-50 border-slate-200 focus:border-[var(--sb-green)] focus:ring-[var(--sb-green)] rounded-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Experience</label>
                    <Input data-testid="apply-experience-input" value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} className="bg-slate-50 border-slate-200 focus:border-[var(--sb-green)] focus:ring-[var(--sb-green)] rounded-none" placeholder="e.g. 3-5 years" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Current Role</label>
                    <Input data-testid="apply-role-input" value={form.current_role} onChange={(e) => setForm({ ...form, current_role: e.target.value })} className="bg-slate-50 border-slate-200 focus:border-[var(--sb-green)] focus:ring-[var(--sb-green)] rounded-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Preferred Industry</label>
                    <Select value={form.preferred_industry} onValueChange={(val) => setForm({ ...form, preferred_industry: val })}>
                      <SelectTrigger data-testid="apply-industry-select" className="bg-slate-50 border-slate-200 rounded-none">
                        <SelectValue placeholder="Select Industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {["IT", "Banking & Finance", "Manufacturing", "Healthcare", "Retail & FMCG", "Construction", "Logistics", "Education", "Pharma", "Other"].map((ind) => (
                          <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Additional Information</label>
                  <Textarea data-testid="apply-message-textarea" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-slate-50 border-slate-200 focus:border-[var(--sb-green)] focus:ring-[var(--sb-green)] rounded-none" placeholder="Tell us about your career goals, skills, or anything relevant..." />
                </div>
                <button type="submit" disabled={submitting} data-testid="apply-submit-btn" className="bg-[var(--sb-green)] text-white px-8 py-4 text-base font-bold uppercase tracking-wider hover:bg-[#2E7E2E] transition-all hover:-translate-y-1 inline-flex items-center gap-2 disabled:opacity-50">
                  {submitting ? "Submitting..." : "Submit Profile"} <ArrowRight className="w-5 h-5" />
                </button>
              </form>
            </ScrollReveal>
          )}
        </div>
      </section>

      {/* Tips */}
      <section data-testid="career-tips-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <ScrollReveal className="text-center mb-16">
            <span className="text-sm font-bold tracking-[0.15em] uppercase text-[var(--sb-green)] mb-3 block">Career Advice</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 uppercase">Tips for Job Seekers</h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {tips.map((tip, i) => (
              <ScrollReveal key={tip.title} delay={i}>
                <div className="bg-white border border-slate-200 p-8 transition-all duration-300 hover:shadow-[0_10px_40px_-15px_rgba(58,158,58,0.15)] hover:-translate-y-1 hover:border-[var(--sb-green)]/30 h-full">
                  <div className="w-14 h-14 bg-green-50 flex items-center justify-center mb-6 text-[var(--sb-green)]">
                    <tip.icon className="w-7 h-7" />
                  </div>
                  <h4 className="font-heading text-lg font-bold text-slate-900 uppercase mb-3">{tip.title}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">{tip.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
